import { useState, useEffect } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Layout, Header } from '../../components/mte/shell';
import DashboardMonde from '../DashboardMonde';
import { apiFetch, ApiError } from '../../lib/auth-fetch';
import type { HeroId } from '../../components/mte/placeholders';
import styles from './Aventure.module.css';

/**
 * Aventure - GATE dynamique (entrée /enfant/aventure/).
 *
 * Per Boussole spec ratified Ana 17/09 ~19:40 §11.3
 * (wip/boussole-docs @ 2da29c6).
 *
 * Comportement :
 *   1. fetch GET /api/device/children -> premier enfant actif
 *   2. fetch GET /api/device/children/[id]/diagnostics
 *   3. si diagnostics vide -> redirect /enfant/diagnostic/ (ouvri diagnostic)
 *   4. sinon -> render DashboardMonde (parcours ouvre, hero opcional)
 *
 * Hero est OPTIONNEL ici - jamais bloquant.
 * NE JAMAIS redemander prenom/niveau/hero (contrat routing spec).
 */

interface Child {
  childId: string;
  firstName: string;
  schoolLevel: 'CE1' | 'CM1';
  heroId: HeroId | null;
}

interface DiagnosticRow {
  diagnosticId: string;
  competenceId: string;
  scoreState: string;
  scoreValue: number | null;
  occurredAt: string;
}

type GateState = 'loading' | 'no-children' | 'no-diagnostic' | 'ready' | 'error';

function Aventure(): JSX.Element {
  const [searchParams] = useSearchParams();
  const requestedChildId = searchParams.get('childId');
  const [gateState, setGateState] = useState<GateState>('loading');
  const [activeChild, setActiveChild] = useState<Child | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Step 1: fetch children list
        const childrenResp = await apiFetch<{ children: Child[] }>(
          '/api/parent/children'
        );
        if (cancelled) return;
        const children = childrenResp?.children ?? [];
        if (children.length === 0) {
          // No children registered -> redirect to family home (will show empty state)
          setGateState('no-children');
          return;
        }
        // Use the explicitly selected child when provided; otherwise fall back to the first profile.
        const child = requestedChildId
          ? children.find((candidate) => candidate.childId === requestedChildId)
          : children[0];
        if (!child) {
          setGateState('no-children');
          return;
        }
        setActiveChild(child);

        // Step 2: check if diagnostic is done
        const diagResp = await apiFetch<{ diagnostics: DiagnosticRow[] }>(
          `/api/device/children/${encodeURIComponent(child.childId)}/diagnostics`
        );
        if (cancelled) return;
        const diags = diagResp?.diagnostics ?? [];
        if (diags.length === 0) {
          // No diagnostic yet -> redirect to diagnostic page
          setGateState('no-diagnostic');
          return;
        }
        // Diagnostic completed -> proceed to DashboardMonde
        setGateState('ready');
      } catch (e) {
        if (cancelled) return;
        if (!(e instanceof ApiError)) console.warn('[aventure-gate] failed', e);
        setGateState('error');
        setError(
          e instanceof ApiError
            ? e.message
            : 'Impossible de verifier le diagnostic. Reessayer.'
        );
      }
    })();
    return () => { cancelled = true; };
  }, [requestedChildId]);

  // GATE decisions (per spec §11.3)
  if (gateState === 'loading') {
    return (
      <Layout
        context="enfant"
        width="default"
        header={
          <Header
            context="enfant"
            logo={<>✦ Mon Temps d&apos;Étude</>}
          />
        }
      >
        <main className={styles.main} aria-busy="true" aria-live="polite">
          <p className={styles.loadingMsg}>
            {activeChild
              ? `On prepare l'aventure pour ${activeChild.firstName}...`
              : 'On prepare ton aventure...'}
          </p>
        </main>
      </Layout>
    );
  }

  if (gateState === 'no-children') {
    return <Navigate to="/famille/accueil/" replace />;
  }

  if (gateState === 'no-diagnostic') {
    return <Navigate to={`/enfant/diagnostic/?childId=${encodeURIComponent(activeChild?.childId ?? requestedChildId ?? '')}`} replace />;
  }

  if (gateState === 'error') {
    return (
      <Layout
        context="enfant"
        width="default"
        header={
          <Header
            context="enfant"
            logo={<>✦ Mon Temps d&apos;Étude</>}
          />
        }
      >
        <main className={styles.main} role="alert">
          <p className={styles.errorMsg}>
            {error ?? 'Une erreur est survenue.'}
          </p>
        </main>
      </Layout>
    );
  }

  // gateState === ready -> render DashboardMonde wrapped in nav-hub (M-C2 Workstream C)
  return (
    <Layout
      context="enfant"
      width="default"
      header={
        <Header
          context="enfant"
          logo={<>✦ Mon Temps d&apos;Étude</>}
        />
      }
    >
      <nav className={styles.parcoursRevision} aria-label="Parcours de revision">
        <p className={styles.parcoursRevisionTitle}>Parcours de revision</p>
        <ul className={styles.parcoursRevisionList}>
          <li>
            <Link to="/enfant/mes-pouvoirs/" data-action="open-mes-pouvoirs">
              Mes pouvoirs
            </Link>
          </li>
          <li>
            <Link to="/enfant/diagnostic/" data-action="open-diagnostic-nav">
              Reprendre mon diagnostic
            </Link>
          </li>
          <li>
            <Link to="/enfant/mission/rentree/" data-action="open-mission-rentree">
              Mission Rentree
            </Link>
          </li>
        </ul>
      </nav>
      <DashboardMonde />
    </Layout>
  );
}


export default Aventure;
